'use client';

import { FC, useState } from 'react';
import { useRouter } from 'next/navigation';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Box, Stack, Divider, Container, Typography } from '@mui/material';

import Image from '../../../../components/image';
import { useLocales } from '../../../../locales';
import { ComponentTypeProps } from '../../../../types/page-generator';
import { useResponsive } from '../../../../hooks/use-responsive';

const CryptoDeposit: FC<ComponentTypeProps> = ({ block }) => {
  const [currency, setCurrency] = useState('USDT');
  const [portType, setPortType] = useState('(TRC-20)');
  const { currentLang } = useLocales();
  const router = useRouter();
  const isMobile = useResponsive('down', 'sm');

  const firstStop = block?.settings?.firstGradientColorStop
    ? `${block?.settings?.firstGradientColorStop}%`
    : '40%';
  const secondStop = block?.settings?.secondGradientColorStop
    ? `${block?.settings?.secondGradientColorStop}%`
    : '100%';

  const colorGradient = block?.settings?.addGradientStyle
    ? `linear-gradient(90deg, ${block?.settings?.firstGradientColor} ${firstStop}, ${block?.settings?.secondGradientColor} ${secondStop} )`
    : '';

  const firstAmountStop = block?.settings?.firstAmountGradientColorStop
    ? `${block?.settings?.firstAmountGradientColorStop}%`
    : '40%';
  const secondAmountStop = block?.settings?.secondAmountGradientColorStop
    ? `${block?.settings?.secondAmountGradientColorStop}%`
    : '100%';

  const colorAmountGradient = block?.settings?.addAmountGradientStyle
    ? `linear-gradient(90deg, ${block?.settings?.firstAmountGradientColor} ${firstAmountStop}, ${block?.settings?.secondAmountGradientColor} ${secondAmountStop} )`
    : '';

  const firstLastStop = block?.settings?.firstLastGradientColorStop
    ? `${block?.settings?.firstLastGradientColorStop}%`
    : '40%';
  const secondLastStop = block?.settings?.secondLastGradientColorStop
    ? `${block?.settings?.secondLastGradientColorStop}%`
    : '100%';

  const colorLastGradient = block?.settings?.addSecondGradientStyle
    ? `linear-gradient(90deg, ${block?.settings?.firstLastGradientColor} ${firstLastStop}, ${block?.settings?.secondLastGradientColor} ${secondLastStop} )`
    : '';

  const balances: Record<string, string> = {
    USDT: '0.00',
    EUR: '89.56',
    BTC: '0.0023',
    ETH: '0.0341',
  };

  const portOptions: Record<string, string[]> = {
    USDT: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
    EUR: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
    BTC: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
    ETH: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
  };

  const handleCurrencyChange = (e: any) => {
    const newCurrency = e.target.value;
    setCurrency(newCurrency);
    const ports = portOptions[newCurrency];
    setPortType(ports.length ? ports[0] : '');
  };

  return (
    <Container maxWidth="sm" sx={{ p: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
        }}
      >
        <IconButton color="inherit" onClick={() => router.back()}>
          <ArrowBack sx={{ color: 'black' }} />
        </IconButton>
        <Typography variant="h6" sx={{ color: 'black' }}>
          Deposit
        </Typography>
      </Box>
      {block?.settings?.enableMain && (
        <Stack sx={{ border: '1px dashed black', borderRadius: 4 }}>
          <Box
            sx={{
              width: '100.5%',
              mx: -0.25,
              mt: -0.25,
              borderRadius: 4,
              background: block?.settings?.addGradientStyle
                ? colorGradient
                : block?.settings?.mainColor || '#F55D52',
              padding: 2,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              boxShadow: 6,
              justifyContent: 'center',
              // flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
            }}
          >
            <Box mr={2}>
              <Image
                src="/assets/images/hand-card.png"
                width={isMobile ? 100 : 40}
                height={isMobile ? 100 : 40}
                alt="hand"
              />
            </Box>

            <Box>
              <Select
                value={currency}
                onChange={handleCurrencyChange}
                variant="standard"
                disableUnderline
                sx={{
                  color: 'white',
                  fontSize: 18,
                  fontWeight: 600,
                  mb: 0.5,
                  '& .MuiSelect-icon': { color: 'white', right: '4px' },
                }}
              >
                {Object.keys(balances).map((cur) => (
                  <MenuItem key={cur} value={cur}>
                    {cur}
                  </MenuItem>
                ))}
              </Select>

              <Typography fontSize={12}>Crypto Currency</Typography>

              <Typography fontSize={20} fontWeight={700} mt={1}>
                {balances[currency]} {currency}
              </Typography>

              {portOptions[currency]?.length > 0 && (
                <Select
                  value={portType}
                  onChange={(e) => setPortType(e.target.value)}
                  variant="standard"
                  disableUnderline
                  sx={{
                    color: 'white',
                    fontSize: 14,
                    '& .MuiSelect-icon': { color: 'white', right: '4px' },
                    mt: 0.5,
                  }}
                >
                  {portOptions[currency].map((port) => (
                    <MenuItem key={port} value={port}>
                      Balance {port}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          </Box>
          <Stack alignItems="center" my={2}>
            <Typography>4611XXXX XXXX 6155</Typography>
            <Typography>Card Number</Typography>
          </Stack>
        </Stack>
      )}
      <Box
        sx={{
          mt: 2,
          borderRadius: 4,
          color: 'white',
          mx: 'auto',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {/* Верхняя секция */}
        {block?.settings?.enableDepositAmount && (
          <Box
            sx={{
              background: block?.settings?.addAmountGradientStyle
                ? colorAmountGradient
                : block?.settings?.depositAmountColor || '#5D4A85',
              borderRadius: 3,
              p: 2.5,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontSize={26} fontWeight={500} color="#C1B3D2">
                Deposit Amount
              </Typography>
              <Stack display="flex" alignItems="flex-end" justifyContent="space-between">
                <Typography fontSize={32} fontWeight={600} color="#C1B3D2">
                  0.00
                </Typography>
                <Typography fontSize={14} fontWeight={400} color="#C1B3D2">
                  USDT
                </Typography>
              </Stack>
            </Stack>
          </Box>
        )}
        {block?.settings?.enableDepositAmount && (
          <Stack width={1} p={1} alignItems="center">
            <Typography fontSize={12} color="#C1B3D2">
              Maximum deposit amount is <strong>10,000.00 USDT</strong> /{' '}
              <strong>10,000.00 USD</strong>
              <br />
              Minimum deposit amount is <strong>52.00 USDT</strong> / <strong>52.00 USD</strong>
            </Typography>
          </Stack>
        )}

        {/* Нижняя секция */}
        {block?.settings?.enableSecond && (
          <Box
            sx={{
              background: block?.settings?.addSecondGradientStyle
                ? colorLastGradient
                : block?.settings?.secondColor || '#2B1A3C',
              borderRadius: 3,
              p: 2,
            }}
          >
            {[
              { label: 'Fee', value: '0.00 USDT' },
              { label: 'Estimated Crypto Amount', value: '0.00 USDT' },
              { label: 'Total Receive Currency Amount', value: '0.00 USD' },
            ].map((item, index) => (
              <Box key={item.label} mb={index < 2 ? 1.5 : 0}>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={14} color="#C1B3D2">
                    {item.label}
                  </Typography>
                  <Typography fontSize={14} fontWeight={500}>
                    {item.value}
                  </Typography>
                </Box>
                {index < 2 && (
                  <Divider
                    sx={{
                      borderColor: 'grey',
                      mt: 1,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Подсказка */}
        {block?.settings?.enableSecond && (
          <Stack width={1} p={1} alignItems="center">
            <Typography fontSize={12} color="#A296B0" sx={{ textAlign: 'justify' }}>
              Due to currency price fluctuations, there may be a small difference between the final
              deduction amount and the displayed amount
            </Typography>
          </Stack>
        )}
      </Box>
    </Container>
  );
};
export default CryptoDeposit;
